import { useState } from 'react';
import { payrollLegalApi } from '../../api/services';
import type { ContributionDto, NoveltyTypeDto, PayrollBonusDto, PayrollParameterDto, PayrollTableDto, PayrollTableRowDto, SaveContributionDto, TaxBracketDto } from '../../api/types';
import {
  Alert, Badge, Button, Card, Check, Field, Form, Input, Loadable, Modal, NumberInput, PageHeader, Select, Table, Tabs, Toolbar,
  fmtDate, fmtMoney, todayIso, useAction, useAsync,
} from '../../ui/components';
import { useCountry } from '../../ui/company';
import { PageHelp } from '../../ui/help';

type Tab = 'status' | 'contributions' | 'tax' | 'bonuses' | 'tables' | 'novelties' | 'parameters';

const d10 = (v?: string | null) => (v ? v.slice(0, 10) : '');
const num = (v: number) => Number(v.toFixed(4)).toString();
const MONTHS = ['enero', 'febrero', 'marzo', 'abril', 'mayo', 'junio', 'julio', 'agosto', 'septiembre', 'octubre', 'noviembre', 'diciembre'];
const ReviewBadge = ({ review }: { review: boolean }) => (review ? <Badge tone="warn">Revisar</Badge> : null);

/**
 * Parámetros legales de planilla de la empresa (según su país): cada comercio los revisa y ajusta. Guardar un valor lo
 * da por revisado. Los cambios de ley se registran con fecha de vigencia para no alterar planillas anteriores.
 */
export function PayrollLegalPage() {
  const country = useCountry();
  const [tab, setTab] = useState<Tab>('status');
  return (
    <>
      <PageHeader title="Parámetros legales de planilla" subtitle={country ? `Reglas de ${country.name} con que se calcula su planilla. Su empresa las administra.` : 'Reglas del país con que se calcula su planilla.'} />
      <PageHelp page="payrollLegal" />
      <Tabs<Tab> tabs={[
        { key: 'status', label: 'Resumen' }, { key: 'contributions', label: `Cargas sociales${country ? ` (${country.socialSecurity})` : ''}` },
        { key: 'tax', label: country?.incomeTax ?? 'Impuesto sobre la renta' }, { key: 'bonuses', label: 'Bonos de ley' },
        { key: 'tables', label: 'Vacaciones y liquidación' }, { key: 'novelties', label: 'Tipos de novedad' }, { key: 'parameters', label: 'Otros parámetros' },
      ]} active={tab} onChange={setTab} />
      {tab === 'status' && <StatusTab />}
      {tab === 'contributions' && <ContributionsTab />}
      {tab === 'tax' && <TaxTab />}
      {tab === 'bonuses' && <BonusesTab />}
      {tab === 'tables' && <TablesTab />}
      {tab === 'novelties' && <NoveltiesTab />}
      {tab === 'parameters' && <ParametersTab />}
    </>
  );
}

// ================================================================ Resumen

function StatusTab() {
  const status = useAsync(() => payrollLegalApi.status(), []);
  const [added, setAdded] = useState<string[] | null>(null);
  const { run, busy } = useAction();
  return (
    <Loadable state={status}>
      {(s) => (
        <>
          {s.warnings.map((w) => <Alert key={w} kind="warn">{w}</Alert>)}
          <Card title={s.country ? `País de la empresa: ${s.country.name}` : 'País de la empresa'}>
            <div className="acc-stats">
              <div><strong>{s.contributions}</strong> cargas sociales activas</div>
              <div><strong>{s.taxBrackets}</strong> tramos de renta</div>
              <div><strong>{s.bonuses}</strong> bonos de ley</div>
              <div><strong>{s.tables}</strong> tablas por antigüedad</div>
              <div><strong>{s.noveltyTypes}</strong> tipos de novedad</div>
            </div>
            <p>
              {s.configured
                ? 'Su empresa ya tiene parámetros. Si falta algo del país, use el botón: sólo agrega lo que no tiene y nunca cambia sus valores.'
                : 'Su empresa todavía no tiene parámetros. Cárguelos con el botón y revíselos antes de la primera planilla.'}
            </p>
            <div className="acc-actions" style={{ justifyContent: 'flex-start' }}>
              <Button kind="primary" busy={busy} disabled={!s.country} onClick={() => run(() => payrollLegalApi.loadCountryDefaults(), { success: 'Valores del país revisados.' }).then((r) => { if (r) { setAdded(r.added); status.reload(); } })}>
                Cargar valores del país
              </Button>
            </div>
            {added && (added.length === 0 ? <Alert kind="ok">No faltaba nada: su empresa ya tiene todos los valores del país.</Alert>
              : <Alert kind="ok">Se agregaron: {added.join('; ')}.</Alert>)}
          </Card>
          <Card title="Valores para confirmar con su contador">
            {s.toReview.length === 0
              ? <p>No hay valores pendientes de revisión.</p>
              : (
                <>
                  <p>Estos valores tienen fuentes que no coinciden o dependen de la actividad de su empresa. Ábralos en su pestaña, confírmelos y guárdelos: al guardar dejan de aparecer aquí.</p>
                  <ul>{s.toReview.map((r) => <li key={r} className="acc-review">{r}</li>)}</ul>
                </>
              )}
          </Card>
          <Alert>Los valores iniciales salen de fuentes públicas consultadas en octubre de 2026. La responsabilidad de mantenerlos al día es de cada empresa: cuando cambie la ley, actualícelos aquí con la fecha en que rige el cambio.</Alert>
        </>
      )}
    </Loadable>
  );
}

// ================================================================ Cargas sociales

function ContributionsTab() {
  const list = useAsync(() => payrollLegalApi.contributions(false), []);
  const [editing, setEditing] = useState<ContributionDto | 'new' | null>(null);
  const today = todayIso();
  const vigente = (c: ContributionDto) => c.status === 'A' && (!c.since || d10(c.since) <= today) && (!c.until || d10(c.until) >= today);
  return (
    <>
      <Toolbar><Button kind="primary" onClick={() => setEditing('new')}>Nueva carga</Button></Toolbar>
      <Alert>Cada carga se calcula sobre el salario bruto del período. "Tope" limita la base (p. ej. techo del seguro social) y "Piso" hace que sólo se calcule sobre lo que exceda ese monto. Ambos son montos mensuales.</Alert>
      <Loadable state={list}>
        {(rows) => (
          <Table rows={rows} rowKey={(c) => c.id} onRowClick={setEditing} columns={[
            { key: 'd', title: 'Carga', render: (c) => <>{c.description} <ReviewBadge review={c.review} /></> },
            { key: 'a', title: 'Paga', render: (c) => (c.appliesTo === 'EMPLEADO' ? 'Empleado' : 'Patrono') },
            { key: 'p', title: 'Tasa', align: 'right', render: (c) => `${num(c.percentage)} %` },
            { key: 't', title: 'Tope / piso', render: (c) => [c.ceiling != null ? `tope ${fmtMoney(c.ceiling)}` : '', c.floor != null ? `piso ${fmtMoney(c.floor)}` : ''].filter(Boolean).join(' · ') },
            { key: 'b', title: 'Sobre bonos', render: (c) => (c.onBonus ? `Sí${c.bonusPercentage != null ? ` (${num(c.bonusPercentage)} %)` : ''}` : 'No') },
            { key: 'v', title: 'Vigencia', render: (c) => `${c.since ? fmtDate(c.since) : 'siempre'}${c.until ? ` – ${fmtDate(c.until)}` : ''}` },
            { key: 's', title: 'Estado', render: (c) => (vigente(c) ? <Badge tone="ok">Vigente</Badge> : c.status === 'I' ? <Badge>Inactiva</Badge> : <Badge>Fuera de vigencia</Badge>) },
          ]} />
        )}
      </Loadable>
      {editing && <ContributionForm item={editing === 'new' ? null : editing} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); list.reload(); }} />}
    </>
  );
}

function ContributionForm({ item, onClose, onSaved }: { item: ContributionDto | null; onClose: () => void; onSaved: () => void }) {
  const [v, setV] = useState<SaveContributionDto>(item ? {
    code: item.code ?? '', description: item.description, appliesTo: item.appliesTo, percentage: item.percentage, ceiling: item.ceiling ?? null, floor: item.floor ?? null,
    onBonus: item.onBonus, bonusPercentage: item.bonusPercentage ?? null, since: d10(item.since), until: d10(item.until), status: item.status, note: item.note ?? '',
  } : { code: '', description: '', appliesTo: 'EMPLEADO', percentage: 0, ceiling: null, floor: null, onBonus: false, bonusPercentage: null, since: todayIso(), until: '', status: 'A', note: '' });
  const { run, busy, fieldErrors } = useAction();
  const optNum = (x: string) => (x === '' ? null : Number(x));
  return (
    <Modal title={item ? `Carga: ${item.description}` : 'Nueva carga social'} onClose={onClose} size="lg">
      {item?.note && <Alert kind={item.review ? 'warn' : 'info'}>{item.note}</Alert>}
      <Form onSubmit={() => run(async () => {
        const dto: SaveContributionDto = { ...v, code: v.code?.trim() || null, description: v.description.trim(), since: v.since || null, until: v.until || null, note: v.note?.trim() || null, bonusPercentage: v.onBonus ? v.bonusPercentage : null };
        if (item) await payrollLegalApi.updateContribution(item.id, dto); else await payrollLegalApi.createContribution(dto);
        onSaved();
      }, { success: 'Carga guardada.' })}>
        <div className="acc-grid-form">
          <Field label="Descripción" error={fieldErrors.Description}><Input value={v.description} onChange={(x) => setV({ ...v, description: x })} maxLength={150} required /></Field>
          <Field label="Código" hint="Letras, números y _. Permite llevar la carga a su propia cuenta en la plantilla."><Input value={v.code ?? ''} onChange={(x) => setV({ ...v, code: x.toUpperCase() })} maxLength={30} /></Field>
          <Field label="La paga"><Select value={v.appliesTo} onChange={(x) => setV({ ...v, appliesTo: x })} options={[{ value: 'EMPLEADO', label: 'Empleado (se rebaja del salario)' }, { value: 'PATRONO', label: 'Patrono (costo de la empresa)' }]} /></Field>
          <Field label="Tasa (%)"><NumberInput step="0.0001" value={v.percentage} onChange={(x) => setV({ ...v, percentage: x })} min={0} max={100} /></Field>
          <Field label="Tope mensual de la base" hint="Vacío = sin tope"><Input type="number" value={v.ceiling ?? ''} onChange={(x) => setV({ ...v, ceiling: optNum(x) })} /></Field>
          <Field label="Piso mensual" hint="Sólo sobre lo que exceda este monto. Vacío = todo el salario"><Input type="number" value={v.floor ?? ''} onChange={(x) => setV({ ...v, floor: optNum(x) })} /></Field>
          <Field label="Vigente desde"><Input type="date" value={v.since ?? ''} onChange={(x) => setV({ ...v, since: x })} /></Field>
          <Field label="Vigente hasta" hint="Vacío = sin fecha final"><Input type="date" value={v.until ?? ''} onChange={(x) => setV({ ...v, until: x })} /></Field>
          <Field label="Bonos de ley"><Check checked={v.onBonus} onChange={(x) => setV({ ...v, onBonus: x })} label="También se cobra sobre los bonos de ley" /></Field>
          {v.onBonus && <Field label="Tasa sobre bonos (%)" hint="Vacío = la misma tasa"><Input type="number" value={v.bonusPercentage ?? ''} onChange={(x) => setV({ ...v, bonusPercentage: optNum(x) })} /></Field>}
          <Field label="Estado"><Select value={v.status} onChange={(x) => setV({ ...v, status: x })} options={[{ value: 'A', label: 'Activa' }, { value: 'I', label: 'Inactiva' }]} /></Field>
        </div>
        <Field label="Nota" wide><Input value={v.note ?? ''} onChange={(x) => setV({ ...v, note: x })} maxLength={500} placeholder="Fuente o aclaración (opcional)" /></Field>
        <Alert>Si la ley cambia la tasa, no la modifique aquí: ponga "Vigente hasta" el día anterior al cambio y cree otra carga con el mismo código y la nueva fecha de inicio.</Alert>
        <div className="acc-actions"><Button onClick={onClose}>Cancelar</Button><Button kind="primary" type="submit" busy={busy} disabled={!v.description.trim()}>Guardar</Button></div>
      </Form>
    </Modal>
  );
}

// ================================================================ Renta

function TaxTab() {
  const country = useCountry();
  const list = useAsync(() => payrollLegalApi.taxBrackets(), []);
  const [editing, setEditing] = useState<{ since: string; rows: TaxBracketDto[] } | null>(null);
  const { run, busy } = useAction();
  return (
    <Loadable state={list}>
      {(rows) => {
        const groups = Array.from(new Set(rows.map((r) => d10(r.since)))).sort().reverse();
        return (
          <>
            <Toolbar>
              <Button kind="primary" onClick={() => setEditing({ since: `${new Date().getFullYear() + 1}-01-01`, rows: groups.length ? rows.filter((r) => d10(r.since) === groups[0]) : [{ since: '', from: 0, to: null, percentage: 0 }] })}>Nueva tabla (nuevo año)</Button>
            </Toolbar>
            <Alert>Cada tramo cobra su porcentaje sólo sobre la parte del ingreso que cae dentro del tramo. Se usa la tabla con la fecha de inicio más reciente que no sea posterior al período que se paga.{country?.code === 'CR' ? ' En Costa Rica la tabla es mensual.' : ' La tabla es anual: el sistema proyecta el salario al año.'}</Alert>
            {groups.length === 0 && <Alert kind="warn">No hay tabla de renta: no se retiene impuesto.</Alert>}
            {groups.map((since) => (
              <Card key={since} title={`Vigente desde ${fmtDate(since)}`} actions={<>
                <Button kind="link" onClick={() => setEditing({ since, rows: rows.filter((r) => d10(r.since) === since) })}>Editar</Button>
                <Button kind="link" busy={busy} onClick={() => run(() => payrollLegalApi.deleteTaxBrackets(since), { confirm: `¿Eliminar la tabla vigente desde ${fmtDate(since)}?`, danger: true, success: 'Tabla eliminada.' }).then(list.reload)}>Eliminar</Button>
              </>}>
                <Table rows={rows.filter((r) => d10(r.since) === since)} rowKey={(r) => r.from} columns={[
                  { key: 'f', title: 'Desde', align: 'right', render: (r) => fmtMoney(r.from) },
                  { key: 't', title: 'Hasta', align: 'right', render: (r) => (r.to == null ? 'En adelante' : fmtMoney(r.to)) },
                  { key: 'p', title: 'Porcentaje', align: 'right', render: (r) => (r.percentage === 0 ? 'Exento' : `${num(r.percentage)} %`) },
                ]} />
              </Card>
            ))}
            {editing && <TaxForm initial={editing} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); list.reload(); }} />}
          </>
        );
      }}
    </Loadable>
  );
}

function TaxForm({ initial, onClose, onSaved }: { initial: { since: string; rows: TaxBracketDto[] }; onClose: () => void; onSaved: () => void }) {
  const [since, setSince] = useState(initial.since);
  const [rows, setRows] = useState(initial.rows.map((r) => ({ from: r.from, to: r.to ?? null, percentage: r.percentage })));
  const { run, busy } = useAction();
  const set = (i: number, patch: Partial<{ from: number; to: number | null; percentage: number }>) => setRows((rs) => rs.map((r, k) => (k === i ? { ...r, ...patch } : r)));
  return (
    <Modal title="Tabla de renta" onClose={onClose} size="lg">
      <Form onSubmit={() => run(async () => { await payrollLegalApi.saveTaxBrackets(since, rows); onSaved(); }, { success: 'Tabla guardada.' })}>
        <Field label="Vigente desde"><Input type="date" value={since} onChange={setSince} required /></Field>
        <Table rows={rows.map((r, i) => ({ ...r, i }))} rowKey={(r) => r.i} columns={[
          { key: 'f', title: 'Desde', render: (r) => <NumberInput value={r.from} onChange={(x) => set(r.i, { from: x })} min={0} /> },
          { key: 't', title: 'Hasta (vacío en el último)', render: (r) => <Input type="number" value={r.to ?? ''} onChange={(x) => set(r.i, { to: x === '' ? null : Number(x) })} /> },
          { key: 'p', title: 'Porcentaje', render: (r) => <NumberInput step="0.01" value={r.percentage} onChange={(x) => set(r.i, { percentage: x })} min={0} max={100} /> },
          { key: 'x', title: '', render: (r) => <Button kind="link" disabled={rows.length <= 1} onClick={() => setRows((rs) => rs.filter((_, k) => k !== r.i))}>Quitar</Button> },
        ]} />
        <div className="acc-actions" style={{ justifyContent: 'flex-start' }}>
          <Button onClick={() => setRows((rs) => [...rs, { from: rs.length ? rs[rs.length - 1].to ?? 0 : 0, to: null, percentage: 0 }])}>Agregar tramo</Button>
        </div>
        <Alert>El primer tramo empieza en 0; cada tramo empieza donde termina el anterior; el último no tiene "hasta".</Alert>
        <div className="acc-actions"><Button onClick={onClose}>Cancelar</Button><Button kind="primary" type="submit" busy={busy} disabled={!since}>Guardar</Button></div>
      </Form>
    </Modal>
  );
}

// ================================================================ Bonos de ley

function BonusesTab() {
  const list = useAsync(() => payrollLegalApi.bonuses(), []);
  const [editing, setEditing] = useState<PayrollBonusDto | 'new' | null>(null);
  const period = (b: PayrollBonusDto) => {
    const end = (b.startMonth + b.months - 2) % 12;
    return `${MONTHS[b.startMonth - 1]} a ${MONTHS[end]}`;
  };
  return (
    <>
      <Toolbar><Button kind="primary" onClick={() => setEditing('new')}>Nuevo bono</Button></Toolbar>
      <Loadable state={list}>
        {(rows) => (
          <Table rows={rows} rowKey={(b) => b.id} onRowClick={setEditing} columns={[
            { key: 'd', title: 'Bono', render: (b) => <>{b.description} <ReviewBadge review={b.review} /></> },
            { key: 'p', title: 'Período que acumula', render: (b) => period(b) },
            { key: 'c', title: 'Cálculo', render: (b) => `salarios del período ÷ ${num(b.divisor)}` },
            { key: 'g', title: 'Cargas sociales', render: (b) => (b.contributions ? 'Sí' : 'No') },
            { key: 'r', title: 'Renta', render: (b) => (b.incomeTax ? `Sí${b.exemptAmount != null ? ` (exento hasta ${fmtMoney(b.exemptAmount)})` : ''}` : 'No') },
            { key: 's', title: 'Estado', render: (b) => <Badge tone={b.status === 'A' ? 'ok' : 'neutral'}>{b.status === 'A' ? 'Activo' : 'Inactivo'}</Badge> },
          ]} />
        )}
      </Loadable>
      {editing && <BonusForm item={editing === 'new' ? null : editing} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); list.reload(); }} />}
    </>
  );
}

function BonusForm({ item, onClose, onSaved }: { item: PayrollBonusDto | null; onClose: () => void; onSaved: () => void }) {
  const [v, setV] = useState<Omit<PayrollBonusDto, 'id' | 'review'>>(item ? { ...item } : { code: '', description: '', startMonth: 1, months: 12, divisor: 12, contributions: false, incomeTax: false, exemptAmount: null, status: 'A', note: '' });
  const { run, busy } = useAction();
  const months = MONTHS.map((m, i) => ({ value: i + 1, label: m }));
  return (
    <Modal title={item ? item.description : 'Nuevo bono de ley'} onClose={onClose}>
      {item?.note && <Alert kind={item.review ? 'warn' : 'info'}>{item.note}</Alert>}
      <Form onSubmit={() => run(async () => { await payrollLegalApi.saveBonus({ ...v, code: v.code.trim().toUpperCase(), description: v.description.trim(), note: v.note?.trim() || null }); onSaved(); }, { success: 'Bono guardado.' })}>
        <div className="acc-grid-form">
          <Field label="Código"><Input value={v.code} onChange={(x) => setV({ ...v, code: x.toUpperCase() })} maxLength={20} required disabled={!!item} /></Field>
          <Field label="Descripción"><Input value={v.description} onChange={(x) => setV({ ...v, description: x })} maxLength={150} required /></Field>
          <Field label="Mes en que empieza a acumular"><Select value={v.startMonth} onChange={(x) => setV({ ...v, startMonth: x })} options={months} /></Field>
          <Field label="Meses que acumula"><NumberInput step="1" value={v.months} onChange={(x) => setV({ ...v, months: Math.max(1, Math.min(12, Math.round(x))) })} min={1} max={12} /></Field>
          <Field label="Divisor" hint="Bono = salarios del período ÷ divisor"><NumberInput step="0.0001" value={v.divisor} onChange={(x) => setV({ ...v, divisor: x })} min={0} /></Field>
          <Field label="Cargas sociales"><Check checked={v.contributions} onChange={(x) => setV({ ...v, contributions: x })} label="Paga las cargas marcadas 'sobre bonos'" /></Field>
          <Field label="Renta"><Check checked={v.incomeTax} onChange={(x) => setV({ ...v, incomeTax: x })} label="Paga impuesto sobre la renta" /></Field>
          {v.incomeTax && <Field label="Monto exento de renta" hint="Vacío = todo paga renta"><Input type="number" value={v.exemptAmount ?? ''} onChange={(x) => setV({ ...v, exemptAmount: x === '' ? null : Number(x) })} /></Field>}
          <Field label="Estado"><Select value={v.status} onChange={(x) => setV({ ...v, status: x })} options={[{ value: 'A', label: 'Activo' }, { value: 'I', label: 'Inactivo' }]} /></Field>
        </div>
        <Field label="Nota" wide><Input value={v.note ?? ''} onChange={(x) => setV({ ...v, note: x })} maxLength={500} /></Field>
        <div className="acc-actions"><Button onClick={onClose}>Cancelar</Button><Button kind="primary" type="submit" busy={busy} disabled={!v.code.trim() || !v.description.trim()}>Guardar</Button></div>
      </Form>
    </Modal>
  );
}

// ================================================================ Tablas por antigüedad

const TABLE_INFO: Record<string, string> = {
  VACACIONES: 'Días de vacaciones que se ganan por año, según la antigüedad.',
  CESANTIA: 'Lo que se paga por despido sin justa causa (cesantía o indemnización).',
  PRIMA: 'Prima de antigüedad: se paga en cualquier salida si está marcada "siempre".',
  PREAVISO: 'Preaviso que se paga si no se avisó con anticipación.',
};
const UNITS: Record<string, string> = { D: 'días', S: 'semanas', M: 'meses' };
const MODES: Record<string, string> = { F: 'Cantidad fija', A: 'Por año (tasa según antigüedad total)', T: 'Por cada año dentro del rango' };
const months2text = (m: number) => (m % 12 === 0 ? `${m / 12} año(s)` : `${m} meses`);

function TablesTab() {
  const list = useAsync(() => payrollLegalApi.tables(), []);
  const [editing, setEditing] = useState<PayrollTableDto | null>(null);
  return (
    <Loadable state={list}>
      {(rows) => (
        <>
          {rows.length === 0 && <Alert kind="warn">No hay tablas: cargue los valores del país en la pestaña Resumen.</Alert>}
          {rows.map((t) => (
            <Card key={t.id} title={t.description} actions={<Button kind="link" onClick={() => setEditing(t)}>Editar</Button>}>
              <p><small>{TABLE_INFO[t.code] ?? ''} Unidad: {UNITS[t.unit] ?? t.unit}{t.capYears != null ? ` · máximo ${num(t.capYears)} años` : ''}{t.code !== 'VACACIONES' ? ` · salario promedio de los últimos ${t.averageMonths} meses` : ''}{t.always ? ' · se paga en cualquier salida' : ''}{t.status === 'I' ? ' · INACTIVA' : ''} <ReviewBadge review={t.review} /></small></p>
              {t.note && <p><small>{t.note}</small></p>}
              <Table rows={t.rows} rowKey={(r) => r.fromMonths} columns={[
                { key: 'f', title: 'Antigüedad desde', render: (r) => months2text(r.fromMonths) },
                { key: 't', title: 'Hasta', render: (r) => (r.toMonths == null ? 'En adelante' : months2text(r.toMonths)) },
                { key: 'q', title: 'Cantidad', align: 'right', render: (r) => `${num(r.quantity)} ${UNITS[t.unit] ?? ''}` },
                { key: 'm', title: 'Cómo se aplica', render: (r) => MODES[r.mode] ?? r.mode },
              ]} />
            </Card>
          ))}
          {editing && <TableForm table={editing} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); list.reload(); }} />}
        </>
      )}
    </Loadable>
  );
}

function TableForm({ table, onClose, onSaved }: { table: PayrollTableDto; onClose: () => void; onSaved: () => void }) {
  const [v, setV] = useState({ ...table });
  const [rows, setRows] = useState<PayrollTableRowDto[]>(table.rows.map((r) => ({ ...r })));
  const { run, busy } = useAction();
  const set = (i: number, patch: Partial<PayrollTableRowDto>) => setRows((rs) => rs.map((r, k) => (k === i ? { ...r, ...patch } : r)));
  return (
    <Modal title={table.description} onClose={onClose} size="lg">
      {table.note && <Alert kind={table.review ? 'warn' : 'info'}>{table.note}</Alert>}
      <Form onSubmit={() => run(async () => {
        await payrollLegalApi.saveTable({ code: v.code, description: v.description.trim(), unit: v.unit, capYears: v.capYears ?? null, averageMonths: v.averageMonths, always: v.always, status: v.status, note: v.note?.trim() || null, rows });
        onSaved();
      }, { success: 'Tabla guardada.' })}>
        <div className="acc-grid-form">
          <Field label="Descripción"><Input value={v.description} onChange={(x) => setV({ ...v, description: x })} maxLength={150} required /></Field>
          <Field label="Unidad"><Select value={v.unit} onChange={(x) => setV({ ...v, unit: x })} options={Object.entries(UNITS).map(([value, label]) => ({ value, label }))} /></Field>
          <Field label="Máximo de años" hint="Vacío = sin límite"><Input type="number" value={v.capYears ?? ''} onChange={(x) => setV({ ...v, capYears: x === '' ? null : Number(x) })} /></Field>
          {v.code !== 'VACACIONES' && <Field label="Meses para el salario promedio"><NumberInput step="1" value={v.averageMonths} onChange={(x) => setV({ ...v, averageMonths: Math.max(1, Math.round(x)) })} min={1} /></Field>}
          {v.code !== 'VACACIONES' && <Field label="Siempre"><Check checked={v.always} onChange={(x) => setV({ ...v, always: x })} label="Se paga en cualquier salida" /></Field>}
          <Field label="Estado"><Select value={v.status} onChange={(x) => setV({ ...v, status: x })} options={[{ value: 'A', label: 'Activa' }, { value: 'I', label: 'Inactiva' }]} /></Field>
        </div>
        <Table rows={rows.map((r, i) => ({ ...r, i }))} rowKey={(r) => r.i} columns={[
          { key: 'f', title: 'Desde (meses)', render: (r) => <NumberInput step="1" value={r.fromMonths} onChange={(x) => set(r.i, { fromMonths: Math.max(0, Math.round(x)) })} min={0} /> },
          { key: 't', title: 'Hasta (meses, vacío = en adelante)', render: (r) => <Input type="number" value={r.toMonths ?? ''} onChange={(x) => set(r.i, { toMonths: x === '' ? null : Math.round(Number(x)) })} /> },
          { key: 'q', title: `Cantidad (${UNITS[v.unit] ?? ''})`, render: (r) => <NumberInput step="0.0001" value={r.quantity} onChange={(x) => set(r.i, { quantity: x })} min={0} /> },
          { key: 'm', title: 'Cómo se aplica', render: (r) => <Select value={r.mode} onChange={(x) => set(r.i, { mode: x })} options={Object.entries(MODES).filter(([k]) => v.code !== 'VACACIONES' || k !== 'F').map(([value, label]) => ({ value, label }))} /> },
          { key: 'x', title: '', render: (r) => <Button kind="link" disabled={rows.length <= 1} onClick={() => setRows((rs) => rs.filter((_, k) => k !== r.i))}>Quitar</Button> },
        ]} />
        <div className="acc-actions" style={{ justifyContent: 'flex-start' }}>
          <Button onClick={() => setRows((rs) => [...rs, { fromMonths: rs.length ? rs[rs.length - 1].toMonths ?? 0 : 0, toMonths: null, quantity: 0, mode: v.code === 'VACACIONES' ? 'A' : 'F' }])}>Agregar rango</Button>
        </div>
        <Alert>Cantidad fija: se paga esa cantidad si la antigüedad está en el rango. Por año: la cantidad del rango por cada año trabajado. Por cada año dentro del rango: suma de la cantidad por los años que caen en cada rango.</Alert>
        <div className="acc-actions"><Button onClick={onClose}>Cancelar</Button><Button kind="primary" type="submit" busy={busy}>Guardar</Button></div>
      </Form>
    </Modal>
  );
}

// ================================================================ Tipos de novedad

function NoveltiesTab() {
  const list = useAsync(() => payrollLegalApi.noveltyTypes(false), []);
  const [editing, setEditing] = useState<NoveltyTypeDto | 'new' | null>(null);
  return (
    <>
      <Toolbar><Button kind="primary" onClick={() => setEditing('new')}>Nuevo tipo</Button></Toolbar>
      <Alert>El factor multiplica el valor de la hora: 1,5 = tiempo y medio; 2 = doble; un factor negativo rebaja el salario (p. ej. −1 una incapacidad que paga la seguridad social).</Alert>
      <Loadable state={list}>
        {(rows) => (
          <Table rows={rows} rowKey={(t) => t.code} onRowClick={setEditing} columns={[
            { key: 'd', title: 'Tipo', render: (t) => <>{t.description} <ReviewBadge review={t.review} /></> },
            { key: 'f', title: 'Factor', align: 'right', render: (t) => num(t.factor) },
            { key: 'v', title: 'Vacaciones', render: (t) => (t.isVacation ? 'Cuenta como día de vacaciones' : '') },
            { key: 's', title: 'Estado', render: (t) => <Badge tone={t.status === 'A' ? 'ok' : 'neutral'}>{t.status === 'A' ? 'Activo' : 'Inactivo'}</Badge> },
          ]} />
        )}
      </Loadable>
      {editing && <NoveltyForm item={editing === 'new' ? null : editing} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); list.reload(); }} />}
    </>
  );
}

function NoveltyForm({ item, onClose, onSaved }: { item: NoveltyTypeDto | null; onClose: () => void; onSaved: () => void }) {
  const [v, setV] = useState<Omit<NoveltyTypeDto, 'review'>>(item ? { ...item } : { code: '', description: '', factor: 1.5, isVacation: false, status: 'A', note: '' });
  const { run, busy } = useAction();
  return (
    <Modal title={item ? item.description : 'Nuevo tipo de novedad'} onClose={onClose} size="sm">
      {item?.note && <Alert kind={item.review ? 'warn' : 'info'}>{item.note}</Alert>}
      <Form onSubmit={() => run(async () => { await payrollLegalApi.saveNoveltyType({ ...v, code: v.code.trim().toUpperCase(), description: v.description.trim(), note: v.note?.trim() || null }); onSaved(); }, { success: 'Tipo guardado.' })}>
        <Field label="Código"><Input value={v.code} onChange={(x) => setV({ ...v, code: x.toUpperCase() })} maxLength={20} required disabled={!!item} /></Field>
        <Field label="Descripción"><Input value={v.description} onChange={(x) => setV({ ...v, description: x })} maxLength={150} required /></Field>
        <Field label="Factor" hint="Entre −1 y 5"><NumberInput step="0.0001" value={v.factor} onChange={(x) => setV({ ...v, factor: x })} min={-1} max={5} /></Field>
        <Check checked={v.isVacation} onChange={(x) => setV({ ...v, isVacation: x })} label="Cuenta como día de vacaciones (va a la línea de vacaciones del asiento)" />
        <Field label="Estado"><Select value={v.status} onChange={(x) => setV({ ...v, status: x })} options={[{ value: 'A', label: 'Activo' }, { value: 'I', label: 'Inactivo' }]} /></Field>
        <Field label="Nota"><Input value={v.note ?? ''} onChange={(x) => setV({ ...v, note: x })} maxLength={500} /></Field>
        <div className="acc-actions"><Button onClick={onClose}>Cancelar</Button><Button kind="primary" type="submit" busy={busy} disabled={!v.code.trim() || !v.description.trim()}>Guardar</Button></div>
      </Form>
    </Modal>
  );
}

// ================================================================ Otros parámetros

const PARAMETER_CODES: { code: string; label: string; text?: boolean }[] = [
  { code: 'RENTA_PERIODO', label: 'Período de la tabla de renta', text: true },
  { code: 'RENTA_CREDITO_HIJO', label: 'Crédito fiscal mensual por hijo' },
  { code: 'RENTA_CREDITO_CONYUGE', label: 'Crédito fiscal mensual por cónyuge' },
  { code: 'RENTA_DEDUCCION_ANUAL', label: 'Deducción anual antes de la tabla de renta' },
  { code: 'RENTA_DEDUCE_CARGAS', label: 'Restar cargas del empleado antes de la renta (1 sí / 0 no)' },
  { code: 'LIQ_CONCEPTOS_CARGAS', label: 'Conceptos de la liquidación que pagan cargas sociales', text: true },
];

const SETTLEMENT_CONCEPTS = [
  { value: 'VACACIONES', label: 'Vacaciones' }, { value: 'AGUINALDO', label: 'Bonos de ley proporcionales' }, { value: 'PREAVISO', label: 'Preaviso' },
  { value: 'CESANTIA', label: 'Cesantía / indemnización' }, { value: 'PRIMA', label: 'Prima de antigüedad' },
];

/** Selección múltiple de conceptos de la liquidación (se guarda como texto separado por comas; NINGUNO = ninguno). */
function ConceptChecks({ value, onChange }: { value: string; onChange: (v: string) => void }) {
  const selected = value.split(',').map((x) => x.trim()).filter((x) => x && x !== 'NINGUNO');
  const toggle = (code: string, on: boolean) => {
    const next = on ? [...selected, code] : selected.filter((x) => x !== code);
    onChange(next.length === 0 ? 'NINGUNO' : SETTLEMENT_CONCEPTS.map((c) => c.value).filter((c) => next.includes(c)).join(','));
  };
  return <>{SETTLEMENT_CONCEPTS.map((c) => <Check key={c.value} checked={selected.includes(c.value)} onChange={(on) => toggle(c.value, on)} label={c.label} />)}</>;
}

function ParametersTab() {
  const list = useAsync(() => payrollLegalApi.parameters(), []);
  const [editing, setEditing] = useState<PayrollParameterDto | 'new' | null>(null);
  const { run, busy } = useAction();
  return (
    <>
      <Toolbar><Button kind="primary" onClick={() => setEditing('new')}>Nuevo valor</Button></Toolbar>
      <Loadable state={list}>
        {(rows) => (
          <Table rows={rows} rowKey={(p) => `${p.code}-${p.since}`} onRowClick={setEditing} columns={[
            { key: 'l', title: 'Parámetro', render: (p) => <>{p.label} <ReviewBadge review={p.review} /></> },
            { key: 's', title: 'Vigente desde', render: (p) => fmtDate(p.since) },
            { key: 'v', title: 'Valor', align: 'right', render: (p) => p.text ?? (p.value == null ? '' : num(p.value)) },
            { key: 'n', title: 'Nota', render: (p) => <small>{p.note ?? ''}</small> },
            { key: 'x', title: '', render: (p) => <span onClick={(e) => e.stopPropagation()}><Button kind="link" busy={busy} onClick={() => run(() => payrollLegalApi.deleteParameter(p.code, p.since), { confirm: `¿Eliminar "${p.label}" vigente desde ${fmtDate(p.since)}?`, danger: true, success: 'Valor eliminado.' }).then(list.reload)}>Eliminar</Button></span> },
          ]} />
        )}
      </Loadable>
      {editing && <ParameterForm item={editing === 'new' ? null : editing} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); list.reload(); }} />}
    </>
  );
}

function ParameterForm({ item, onClose, onSaved }: { item: PayrollParameterDto | null; onClose: () => void; onSaved: () => void }) {
  const [v, setV] = useState({ code: item?.code ?? 'RENTA_PERIODO', since: d10(item?.since) || todayIso(), value: item?.value ?? null as number | null, text: item?.text ?? '', note: item?.note ?? '' });
  const { run, busy } = useAction();
  const def = PARAMETER_CODES.find((p) => p.code === v.code);
  return (
    <Modal title={item ? item.label : 'Nuevo valor de parámetro'} onClose={onClose} size="sm">
      {item?.note && <Alert kind={item.review ? 'warn' : 'info'}>{item.note}</Alert>}
      <Form onSubmit={() => run(async () => { await payrollLegalApi.saveParameter({ code: v.code, since: v.since, value: def?.text ? null : v.value, text: def?.text ? v.text : null, note: v.note.trim() || null }); onSaved(); }, { success: 'Valor guardado.' })}>
        <Field label="Parámetro"><Select value={v.code} onChange={(x) => setV({ ...v, code: x })} options={PARAMETER_CODES.map((p) => ({ value: p.code, label: p.label }))} disabled={!!item} /></Field>
        <Field label="Vigente desde"><Input type="date" value={v.since} onChange={(x) => setV({ ...v, since: x })} required disabled={!!item} /></Field>
        {v.code === 'LIQ_CONCEPTOS_CARGAS'
          ? <Field label="Pagan cargas sociales al liquidar" hint="Bonos de ley usan la tasa de bonos de cada carga"><ConceptChecks value={v.text} onChange={(x) => setV({ ...v, text: x })} /></Field>
          : def?.text
          ? <Field label="Valor"><Select value={v.text} onChange={(x) => setV({ ...v, text: x })} options={[{ value: 'MENSUAL', label: 'Mensual' }, { value: 'ANUAL', label: 'Anual' }]} /></Field>
          : <Field label="Valor"><Input type="number" value={v.value ?? ''} onChange={(x) => setV({ ...v, value: x === '' ? null : Number(x) })} /></Field>}
        <Field label="Nota"><Input value={v.note} onChange={(x) => setV({ ...v, note: x })} maxLength={500} /></Field>
        <div className="acc-actions"><Button onClick={onClose}>Cancelar</Button><Button kind="primary" type="submit" busy={busy}>Guardar</Button></div>
      </Form>
    </Modal>
  );
}
